import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * The carrier's own pages, in the look Brian reviewed: the Carrier Dashboard Schematic's boards
 * (version 38). Brian, 2026-10-28: "super simple, very little icons", one view per click, and
 * "Complexity can happen on the back end with us."
 *
 * The cards' rules, which every page here keeps:
 *   - titles, not statements; text under a title describes the data and never talks to the reader;
 *   - a label and an Edit link, not a sentence;
 *   - nothing said twice on a page.
 *
 * ⛔ AND ONE RULE OF OUR OWN: a page shows real data or says plainly that nothing feeds it yet. The
 * cards were drawn with sample numbers; these pages never are. A control whose engine is not built
 * keeps its place and its setting, marked <NotRunning /> (docs/freight-carrier-pages-plan-2026-10.md).
 */

// The cards' colours. `muted` is 6.6:1 on white and 6.1:1 on the page, so grey text that carries
// information still reads.
export const C = {
  ink: "#1B1B1B", muted: "#5C5C5C", faint: "#6E6E69", line: "#DADAD6", soft: "#ECECE8", field: "#BDBDB8",
  page: "#F6F6F4", card: "#FFFFFF", accent: "#1F4FD1", accentDark: "#163A9C", accentWash: "#E3EAFB",
  accentLight: "#9DB2EE", good: "#006300", bad: "#B3261E",
};
export const font = `Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;

/** The words for each stage, where a carrier that is not live says what Alfred will and will not do. */
export const STAGE_BANNER = {
  setup: "Your account is still being set up. Alfred calls nobody yet.",
  rehearsal: "Rehearsal: until you go live, Alfred calls only the test phones MuleWorks marked and AI demonstration drivers and applicants.",
  paused: "Your account is paused. Alfred calls nobody until it is resumed.",
};

/**
 * Inter, as the schematic is drawn. ⛔ ADDED BY THESE PAGES ONLY, never in index.html: the public
 * application page (/apply) must not send an applicant's address to a font host.
 */
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
function useInter() {
  useEffect(() => {
    if (typeof document === "undefined" || document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = FONT_HREF;
    document.head.appendChild(l);
  }, []);
}

/** What went wrong, in the server's words when it gave any. */
export const errText = (e) => e?.body?.message || e?.body?.note || e?.message || "That did not work. Try again.";

const CSS = `
  .cp-root a { color: ${C.accent}; }
  .cp-root a:hover { color: ${C.accentDark}; }
  .cp-root a:focus-visible, .cp-root button:focus-visible, .cp-root input:focus-visible,
  .cp-root select:focus-visible, .cp-root textarea:focus-visible, .cp-root summary:focus-visible {
    outline: 2px solid ${C.accent}; outline-offset: 2px; }
  .cp-root input::placeholder, .cp-root textarea::placeholder { color: ${C.faint}; }
  .cp-row:hover { background: #FAFAF8 !important; }
  .cp-narrow { display: none; }
  @media (max-width: 640px) { .cp-wide { display: none; } .cp-narrow { display: block; } }
  .cp-head { padding: 0 32px; }
  .cp-main { padding: 40px 24px 64px; }
  @media (max-width: 600px) {
    .cp-head { padding: 0 16px; }
    .cp-main { padding: 24px 16px 48px; }
    .cp-h1 { font-size: 28px !important; }
  }
`;

/**
 * Every page's frame: the header (company, and Settings) and one centred column. `company` is the
 * carrier's name; `settings` false hides the link (the opt-in page has none).
 */
export function Shell({ company, settings = true, stage = null, children, gap = 24, width = 880, brandTo = "/carrier" }) {
  useInter();
  return (
    <div className="cp-root" style={{ minHeight: "100vh", background: C.page, fontFamily: font, color: C.ink, display: "flex", flexDirection: "column", WebkitFontSmoothing: "antialiased" }}>
      <style>{CSS}</style>
      <header className="cp-head" style={{ minHeight: 64, background: C.card, borderBottom: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 18 }}>
          {brandTo ? <Link to={brandTo} style={{ color: C.ink, textDecoration: "none" }}><span style={{ fontWeight: 700 }}>Alfred</span></Link> : <span style={{ fontWeight: 700 }}>Alfred</span>}
          {company ? <span style={{ color: C.muted }}> · {company}</span> : null}
        </span>
        {settings && <Link to="/carrier/settings" style={{ fontSize: 15, fontWeight: 500, textDecoration: "none" }}>Settings</Link>}
      </header>
      {STAGE_BANNER[stage] && (
        <div role="status" style={{ background: C.accentWash, color: C.ink, fontSize: 15, padding: "10px 24px", textAlign: "center", lineHeight: 1.45 }}>
          {STAGE_BANNER[stage]}
        </div>
      )}
      <main className="cp-main" style={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: width, display: "flex", flexDirection: "column", gap }}>{children}</div>
      </main>
    </div>
  );
}

export function Back({ to, children }) {
  return <Link to={to} style={{ fontSize: 15, fontWeight: 500, textDecoration: "none", alignSelf: "flex-start" }}>← {children}</Link>;
}

export function Title({ children, sub }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <h1 className="cp-h1" style={{ margin: 0, fontSize: 34, lineHeight: 1.2, fontWeight: 600 }}>{children}</h1>
      {sub ? <p style={{ margin: 0, fontSize: 16, color: C.muted }}>{sub}</p> : null}
    </div>
  );
}

export function Section({ title, sub, right, children, id, gap = 12 }) {
  return (
    <section aria-labelledby={id} style={{ display: "flex", flexDirection: "column", gap }}>
      {(title || right) && (
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {title && <h2 id={id} style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h2>}
            {sub ? <p style={{ margin: 0, fontSize: 15, color: C.muted }}>{sub}</p> : null}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

/** A white card. */
export function Card({ children, style, strong }) {
  return (
    <div style={{ background: C.card, border: strong ? `2px solid ${C.accent}` : `1px solid ${C.line}`, borderRadius: 12, ...style }}>{children}</div>
  );
}

const buttonLook = (kind) => (kind === "secondary"
  ? { color: C.ink, background: C.card, border: `1px solid ${C.field}` }
  : kind === "quiet"
    ? { color: C.accent, background: "transparent", border: "1px solid transparent" }
    : { color: "#FFFFFF", background: C.accent, border: `1px solid ${C.accent}` });

export function Button({ children, onClick, kind = "primary", disabled, type = "button", label, style, expanded }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} aria-label={label} aria-expanded={expanded}
      style={{ ...buttonLook(kind), minHeight: 44, padding: "0 18px", font: "inherit", fontSize: 15, fontWeight: 600, borderRadius: 8,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1, ...style }}>
      {children}
    </button>
  );
}

/** A link that looks like the cards' Open button. */
export function LinkButton({ to, children, kind = "primary", label }) {
  return (
    <Link to={to} aria-label={label} style={{ ...buttonLook(kind), display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 44,
      padding: "0 20px", borderRadius: 8, fontSize: 15, fontWeight: 600, textDecoration: "none", boxSizing: "border-box", whiteSpace: "nowrap" }}>
      {children}
    </Link>
  );
}

/** One row of a list the cards draw as separate cards: a name, a line under it, and Open. */
export function RowCard({ title, sub, to, action = "Open", strong, label, extra }) {
  return (
    <Card strong={strong} style={{ padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 420px", minWidth: 0 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>{title}</span>
        {sub ? <span style={{ fontSize: 16, color: C.muted, lineHeight: 1.45 }}>{sub}</span> : null}
        {extra}
      </div>
      {to ? <LinkButton to={to} label={label}>{action}</LinkButton> : null}
    </Card>
  );
}

/** Rows inside one card, as the cards' Recent and Drivers lists are drawn. */
export function ListCard({ children, label }) {
  return <Card style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}><ul aria-label={label} style={{ listStyle: "none", margin: 0, padding: 0 }}>{children}</ul></Card>;
}

export function ListRow({ to, name, children, last, action = "Open" }) {
  return (
    <li style={{ borderBottom: last ? "none" : `1px solid ${C.soft}` }}>
      <Link to={to} className="cp-row" style={{ padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px 16px", flexWrap: "wrap", color: C.ink, textDecoration: "none" }}>
        <span style={{ fontSize: 17, fontWeight: 600, minWidth: 140 }}>{name}</span>
        <span style={{ fontSize: 16, color: C.muted, flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>{children}</span>
        <span style={{ fontSize: 15, fontWeight: 600, color: C.accent }}>{action}</span>
      </Link>
    </li>
  );
}

/** Home's tile: the area, a big number, and what the number counts. */
export function Tile({ to, title, value, sub }) {
  return (
    <Link to={to} className="cp-row" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 32, display: "flex", flexDirection: "column", gap: 14,
      color: C.ink, textDecoration: "none", minHeight: 220, boxSizing: "border-box" }}>
      <span style={{ fontSize: 22, fontWeight: 600 }}>{title}</span>
      <span style={{ fontSize: 64, lineHeight: 1, fontWeight: 700, color: C.accent }}>{value}</span>
      <span style={{ fontSize: 17, color: C.muted }}>{sub}</span>
    </Link>
  );
}

/** One entry on the Settings page: a name and what it holds. */
export function NavCard({ to, title, sub }) {
  return (
    <Link to={to} className="cp-row" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 4,
      color: C.ink, textDecoration: "none" }}>
      <span style={{ fontSize: 18, fontWeight: 600, color: C.accent }}>{title}</span>
      <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.45 }}>{sub}</span>
    </Link>
  );
}

/** A number with its label, as the reports draw them. `tone` colours the line under it. */
export function Stat({ label, value, sub, tone }) {
  const color = tone === "good" ? C.good : tone === "bad" ? C.bad : C.muted;
  return (
    <Card style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 6, flex: "1 1 180px", minWidth: 0 }}>
      <span style={{ fontSize: 15, color: C.muted }}>{label}</span>
      <span style={{ fontSize: 34, lineHeight: 1.1, fontWeight: 700 }}>{value}</span>
      {sub ? <span style={{ fontSize: 14, color, lineHeight: 1.4 }}>{sub}</span> : null}
    </Card>
  );
}

/** The plain line under a list when it is empty, or while it loads. */
export function Quiet({ children }) {
  return <p style={{ margin: 0, fontSize: 16, color: C.muted, lineHeight: 1.5 }}>{children}</p>;
}

/**
 * ⛔ THE MARK ON A SETTING NOTHING READS YET. Stored, shown, and honest about it: Alfred does not act
 * on it until the piece behind it is built (the plan's "Not in this build").
 */
export function NotRunning({ children = "Not running yet" }) {
  return (
    <span style={{ fontSize: 13, fontWeight: 600, color: C.muted, border: `1px solid ${C.line}`, background: C.page, borderRadius: 999, padding: "2px 10px", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

/** What the server said after an action. Always mounted, so a screen reader hears a new message. */
export function Note({ msg }) {
  return (
    <div role="status" aria-live="polite" style={{ fontSize: 15, color: msg && !msg.ok ? C.bad : C.muted, lineHeight: 1.45, minHeight: msg ? undefined : 0 }}>
      {msg ? msg.text : null}
    </div>
  );
}

const fieldBox = { font: "inherit", fontSize: 16, color: C.ink, background: C.card, border: `1px solid ${C.field}`, borderRadius: 8,
  padding: "0 14px", minHeight: 48, boxSizing: "border-box", width: "100%" };

export function Field({ label, value, onChange, type = "text", placeholder, id, hint, autoComplete, inputMode, width }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: width || "100%", minWidth: 0 }}>
      <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600 }}>{label}</label>
      <input id={id} type={type} value={value ?? ""} placeholder={placeholder} autoComplete={autoComplete} inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)} style={fieldBox} />
      {hint ? <p style={{ margin: 0, fontSize: 14, color: C.muted }}>{hint}</p> : null}
    </div>
  );
}

export function TextArea({ label, value, onChange, rows = 3, placeholder, id, hint, maxLength }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600 }}>{label}</label>
      <textarea id={id} rows={rows} value={value ?? ""} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)}
        style={{ ...fieldBox, padding: "12px 14px", lineHeight: 1.5, resize: "vertical" }} />
      {hint ? <p style={{ margin: 0, fontSize: 14, color: C.muted }}>{hint}</p> : null}
    </div>
  );
}

export function Select({ label, value, onChange, options, id, hideLabel, width }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: width || "auto", minWidth: 0 }}>
      {!hideLabel && <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600 }}>{label}</label>}
      <select id={id} aria-label={hideLabel ? label : undefined} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...fieldBox, cursor: "pointer", paddingRight: 8 }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}

/** A pressed / not-pressed row of choices (the reports' Period, Compare and View). */
export function Choices({ label, value, onChange, options, id }) {
  return (
    <div role="group" aria-labelledby={id} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {options.map((o) => {
        const on = o.v === value;
        return (
          <button key={o.v} type="button" aria-pressed={on} onClick={() => onChange(o.v)}
            style={{ minHeight: 40, padding: "0 14px", font: "inherit", fontSize: 15, fontWeight: 600, borderRadius: 8, cursor: "pointer",
              color: on ? "#FFFFFF" : C.ink, background: on ? C.accent : C.card, border: `1px solid ${on ? C.accent : C.field}` }}>
            {o.label}
          </button>
        );
      })}
      <span id={id} hidden>{label}</span>
    </div>
  );
}

/** The cards' tables: a header row, then rows with the first cell as the row's name. */
export const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 16 };
export const th = { textAlign: "left", fontSize: 14, fontWeight: 600, color: C.muted, padding: "12px 16px", borderBottom: `1px solid ${C.line}`, whiteSpace: "nowrap" };
export const td = { padding: "14px 16px", borderBottom: `1px solid ${C.soft}`, verticalAlign: "top", lineHeight: 1.45 };

/** "1 applicant is" / "3 applicants are". */
export const plural = (n, one, many) => (n === 1 ? one : many);

export const clock = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
};

/** "11:42 AM" today, "Yesterday, 4:15 PM", else "Oct 27, 3:14 PM". */
export const when = (iso, now = new Date()) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  const t = clock(iso);
  if (diff === 0) return t;
  if (diff === 1) return `Yesterday, ${t}`;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${t}`;
};

/** A time ahead: "Today, 3:45 PM", "Tomorrow, 9:00 AM", else "Nov 3, 3:45 PM". */
export const dueWhen = (iso, now = new Date()) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const ahead = Math.round((day(d) - day(now)) / 86400000);
  const t = clock(iso);
  if (ahead === 0) return `Today, ${t}`;
  if (ahead === 1) return `Tomorrow, ${t}`;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${t}`;
};
